/**
 * Performance Monitoring Utility
 * Tracks and reports Core Web Vitals and other performance metrics
 */

// Initialize performance monitoring
export const initializePerformanceMonitoring = () => {
  // Track Core Web Vitals
  if ('web-vital' in window) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS(sendToAnalytics);
      getFID(sendToAnalytics);
      getFCP(sendToAnalytics);
      getLCP(sendToAnalytics);
      getTTFB(sendToAnalytics);
    });
  }

  // Track navigation timing
  if (window.performance && window.performance.timing) {
    window.addEventListener('load', () => {
      const navigationTiming = getNavigationTiming();
      console.log('Navigation Timing:', navigationTiming);
      sendToAnalytics({
        name: 'Navigation Timing',
        value: navigationTiming.loadTime,
        rating: navigationTiming.loadTime < 3000 ? 'good' : 'poor'
      });
    });
  }

  // Monitor long tasks
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          console.warn('Long task detected:', {
            duration: entry.duration,
            name: entry.name,
            startTime: entry.startTime
          });
        }
      });
      observer.observe({ entryTypes: ['longtask'] });
    } catch (e) {
      console.log('Long task observer not supported');
    }
  }

  // Track memory usage on supported browsers
  if (performance.memory) {
    setInterval(() => {
      const memoryInfo = {
        usedJSHeapSize: (performance.memory.usedJSHeapSize / 1048576).toFixed(2) + ' MB',
        totalJSHeapSize: (performance.memory.totalJSHeapSize / 1048576).toFixed(2) + ' MB',
        jsHeapSizeLimit: (performance.memory.jsHeapSizeLimit / 1048576).toFixed(2) + ' MB'
      };
      console.log('Memory Info:', memoryInfo);
    }, 30000); // Every 30 seconds
  }
};

/**
 * Get navigation timing metrics
 */
const getNavigationTiming = () => {
  const timing = window.performance.timing;
  const navigation = window.performance.navigation;

  const loadTime = timing.loadEventEnd - timing.navigationStart;
  const domContentLoadedTime = timing.domContentLoadedEventEnd - timing.navigationStart;
  const timeToFirstByte = timing.responseStart - timing.navigationStart;
  const domInteractiveTime = timing.domInteractive - timing.navigationStart;

  return {
    loadTime,
    domContentLoadedTime,
    timeToFirstByte,
    domInteractiveTime,
    redirectCount: navigation.redirectCount,
    type: navigation.type
  };
};

/**
 * Send metrics to analytics service
 * Replace with your actual analytics endpoint
 */
const sendToAnalytics = (metric) => {
  // Example: Send to your analytics service
  if (navigator.sendBeacon) {
    // const data = JSON.stringify(metric);
    // navigator.sendBeacon('/api/analytics', data);
  }
  
  // Or log to console for development
  console.log('Performance Metric:', metric);
};

/**
 * Get current performance metrics
 */
export const getPerformanceMetrics = () => {
  if (!window.performance) return null;

  const timing = window.performance.timing;
  const navigation = window.performance.navigation;

  return {
    navigationStart: timing.navigationStart,
    unloadEventStart: timing.unloadEventStart,
    unloadEventEnd: timing.unloadEventEnd,
    redirectStart: timing.redirectStart,
    redirectEnd: timing.redirectEnd,
    fetchStart: timing.fetchStart,
    domainLookupStart: timing.domainLookupStart,
    domainLookupEnd: timing.domainLookupEnd,
    connectStart: timing.connectStart,
    connectEnd: timing.connectEnd,
    secureConnectionStart: timing.secureConnectionStart,
    requestStart: timing.requestStart,
    responseStart: timing.responseStart,
    responseEnd: timing.responseEnd,
    domLoading: timing.domLoading,
    domInteractive: timing.domInteractive,
    domContentLoadedEventStart: timing.domContentLoadedEventStart,
    domContentLoadedEventEnd: timing.domContentLoadedEventEnd,
    domComplete: timing.domComplete,
    loadEventStart: timing.loadEventStart,
    loadEventEnd: timing.loadEventEnd,
    type: navigation.type,
    redirectCount: navigation.redirectCount
  };
};

/**
 * Measure custom performance metrics
 */
export const measurePerformance = (metricName, callback) => {
  const startMark = `${metricName}-start`;
  const endMark = `${metricName}-end`;
  const measureName = `${metricName}-duration`;

  performance.mark(startMark);
  
  const result = callback();
  
  performance.mark(endMark);
  performance.measure(measureName, startMark, endMark);

  const measure = performance.getEntriesByName(measureName)[0];
  console.log(`${metricName} took ${measure.duration.toFixed(2)}ms`);

  return result;
};

/**
 * Report layout shifts
 */
export const reportLayoutShifts = () => {
  let clsValue = 0;
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (!entry.hadRecentInput) {
        clsValue += entry.value;
        console.log('Cumulative Layout Shift:', clsValue);
      }
    }
  });

  try {
    observer.observe({ type: 'layout-shift', buffered: true });
  } catch (e) {
    console.log('Layout Shift Observer not supported');
  }

  return observer;
};

const performanceMonitoringExport = {
  initializePerformanceMonitoring,
  getPerformanceMetrics,
  measurePerformance,
  reportLayoutShifts
};

export default performanceMonitoringExport;
